import React, { useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Crosshair, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const Section = ({ title, items }) => (
  <div>
    <div className="eyebrow mb-2">{title}</div>
    <ul className="space-y-1.5">
      {(items || []).map((x) => (
        <li key={x} className="text-sm text-slate-700 flex gap-2"><span className="text-navy">▸</span>{x}</li>
      ))}
    </ul>
  </div>
);

export default function CompetitorsPage() {
  const [form, setForm] = useState({ competitor_name: "", competitor_url: "", notes: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const analyze = async () => {
    if (!form.competitor_name) return toast.error("Competitor name required");
    setLoading(true);
    try { const r = await api.post("/competitors/analyze", form); setResult(r.data); toast.success("Analysis complete"); }
    catch { toast.error("Failed"); } finally { setLoading(false); }
  };

  const data = result?.data;

  return (
    <div data-testid="competitors-page">
      <PageHeader eyebrow="Market Intel" title="Know what your competitors do — and beat it." description="AI competitive analysis to find content gaps, weaknesses and ad angles to steal." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="sharp-card p-6">
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider font-semibold">Competitor name</Label><Input data-testid="comp-name" value={form.competitor_name} onChange={(e)=>setForm({...form, competitor_name: e.target.value})} className="rounded-sm mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-semibold">Website</Label><Input data-testid="comp-url" value={form.competitor_url} onChange={(e)=>setForm({...form, competitor_url: e.target.value})} className="rounded-sm mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-semibold">Notes</Label><Textarea data-testid="comp-notes" rows={4} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="rounded-sm mt-1" /></div>
            <Button data-testid="btn-analyze" onClick={analyze} disabled={loading} className="w-full bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11"><Crosshair className="mr-2" size={18} />{loading ? "Analyzing..." : "Analyze competitor"}</Button>
          </div>
        </Card>
        <Card className="sharp-card p-6 lg:col-span-2 min-h-[400px]">
          {!data ? <div className="text-slate-400 text-sm h-full flex items-center justify-center"><Sparkle size={20} className="mr-2" />Analysis will appear here</div> : (
            <div className="space-y-6">
              <div>
                <div className="eyebrow">Summary</div>
                <p className="text-slate-800 mt-1">{data.summary}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Section title="Strengths" items={data.likely_strengths} />
                <Section title="Weaknesses" items={data.likely_weaknesses} />
                <Section title="Content gaps to exploit" items={data.content_gaps_to_exploit} />
                <Section title="Keyword opportunities" items={data.keyword_opportunities} />
                <Section title="Ad angles to try" items={data.ad_angles_to_try} />
                <Section title="Your differentiators" items={data.differentiators_for_us} />
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
